/* ==========================================================================
   Secret Stories PDP - Post-ATC upsell flow (Class Kit CRO PDPs only)
   --------------------------------------------------------------------------
   Loaded by snippets/cro-upsell-flow.liquid, which only renders inside the
   handle gate of sections/main-product-cro-classkit-v1.liquid.

   Intercept mechanism: a document-level CAPTURE-phase click listener runs
   before the target-phase listeners that assets/cro-v1-pdp.js attaches
   directly to every [data-cro-add-to-cart] button. When the upsell root
   [data-cuf-root] exists in the DOM and a context can be built, we call
   preventDefault() + stopImmediatePropagation() so the legacy direct-add
   never fires, and open the sequenced overlay instead:

     Step 1  Matching flashcards (colorway preselected per kit)
     Step 2  Workbook volume tiers (1 / 6 / 30)
     Step 3  Secret Stories App 21-day free trial (selling plan)

   The kit item(s) go into the cart on the click itself, while the first
   step opens, so a shopper who leaves mid-ladder still has the kit in the
   cart (abandoned-cart email, Meta AddToCart). Accept/skip decisions
   accumulate, then ONE POST to /cart/add.js adds every accepted upsell,
   followed by the sections/renderContents drawer flow used by the
   addToCart function in assets/cro-v1-pdp.js.

   Progressive enhancement: if context building throws we return WITHOUT
   preventDefault so the legacy path runs untouched. If the overlay throws
   after interception, we fall back to a direct kit-only add. If the early
   kit add failed, finalize() sends kit + add-ons as one batch and retries
   kit-only, so the purchase is never lost. If an add-on batch fails, the
   add-ons are retried one at a time.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__cufLoaded) return;
  window.__cufLoaded = true;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function fmt(n) {
    return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function fireFlowEvent(name, detail) {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
    } catch (e) {}
  }

  var state = {
    open: false,
    busy: false,
    ctx: null,
    stepIndex: 0,
    accepted: [],
    selectedFc: null,
    selectedWb: null,
    triggerBtn: null,
    lastFocus: null,
    prevBodyOverflow: '',
    kitAdd: null
  };

  // True when the cart already holds the app trial, or when one of these
  // lines is a Value Pack that assets/cart-external-sync.js will answer by
  // injecting the trial. Without that script's list, fall back to the old
  // rule: only Standard kit buyers lack a trial.
  function trialCovered(cartHasTrial, items, selectedKit) {
    if (cartHasTrial) return true;
    var trial = window.ssAppTrial;
    if (!trial || !trial.triggerIds) return selectedKit !== 'standard';
    return items.some(function (it) {
      return trial.triggerIds.indexOf(Number(it.id)) !== -1;
    });
  }

  // ------------------------------------------------------------------
  // Context: parse both config blobs and compute the kit cart item(s)
  // exactly like addToCart in assets/cro-v1-pdp.js would.
  // Re-queried on every ATC click because the color selector re-renders
  // the whole section (Section Rendering API) and replaces this DOM.
  // ------------------------------------------------------------------
  function buildContext() {
    var cufRoot = document.querySelector('[data-cuf-root]');
    var croRoot = document.querySelector('.cro-v1');
    if (!cufRoot || !croRoot) return null;

    var cufConfigEl = cufRoot.querySelector('[data-cuf-config]');
    var croConfigEl = croRoot.querySelector('[data-cro-v1-config]');
    if (!cufConfigEl || !croConfigEl) return null;

    var cufConfig = JSON.parse(cufConfigEl.textContent);
    var croConfig = JSON.parse(croConfigEl.textContent);

    // --- Selected kit + tier state (mirrors cro-v1-pdp.js) ---
    var tiers = {};
    (croConfig.tiers || []).forEach(function (t) { tiers[String(t.id)] = t; });

    var selectedKit = croRoot.getAttribute('data-kit') || croConfig.defaultKit || 'value';
    var atcBtn = croRoot.querySelector('[data-cro-atc]');
    var tierId = (atcBtn && atcBtn.getAttribute('data-cro-tier-id')) ||
      croConfig.defaultTier ||
      (croConfig.tiers && croConfig.tiers[0] && String(croConfig.tiers[0].id));
    var tier = tiers[tierId];

    var hasStarter = !!(croConfig.hasStarter &&
      croRoot.getAttribute('data-has-starter') === 'true' &&
      croConfig.starterAddonVariantId);
    var kitKey = (hasStarter && selectedKit === 'starter') ? 'value' : selectedKit;

    var variantId = null;
    if (tier && tier.variants && tier.variants[kitKey]) variantId = tier.variants[kitKey];
    else if (tier && tier.variantId) variantId = tier.variantId;
    else variantId = croConfig.defaultVariantId;
    if (!variantId) return null;

    var qtyInput = croRoot.querySelector('[data-cro-qty-input]');
    var qty = parseInt(qtyInput && qtyInput.value, 10) || 1;
    if (tier && (!tier.variants || !tier.variants[kitKey]) && tier.cartQty) {
      qty = tier.cartQty * qty;
    }

    // Starter kit = Value Pack variant + Educator Starter Pack add-on,
    // same two-item shape cro-v1-pdp.js posts.
    var kitItems = [{ id: Number(variantId), quantity: qty }];
    if (hasStarter && selectedKit === 'starter') {
      kitItems.push({ id: Number(croConfig.starterAddonVariantId), quantity: qty });
    }

    // --- Which upsell steps exist on this render ---
    // Ladder is host-driven: the snippet emits stepOrder per PDP family
    // (classkit: flashcards>workbook>soundcards>pd>app, flashcards:
    // kits>soundcards>workbook>app, workbook: kits>pd>app, bundle:
    // soundcards>workbook>app). Each step still requires its config to be
    // present and populated. The app step shows whenever the cart will not
    // hold a trial anyway; it used to require a Standard kit, so pages
    // without a kit picker (Phonemic Awareness Cards) never offered it.
    // next() re-checks it against the add-ons accepted on the way.
    var cartHasTrial = !!cufConfig.cartHasTrial;
    var order = cufConfig.stepOrder || ['flashcards', 'workbook', 'pd', 'app'];
    var steps = [];
    order.forEach(function (name) {
      if (name === 'flashcards' && cufConfig.flashcards && cufConfig.flashcards.length) steps.push(name);
      else if (name === 'kits' && cufConfig.kits && cufConfig.kits.items && cufConfig.kits.items.length) steps.push(name);
      else if (name === 'workbook' && cufConfig.workbook && cufConfig.workbook.tiers && cufConfig.workbook.tiers.length) steps.push(name);
      else if (name === 'soundcards' && cufConfig.soundcards && cufConfig.soundcards.variantId) steps.push(name);
      else if (name === 'pd' && cufConfig.pd && cufConfig.pd.variantId) steps.push(name);
      else if (name === 'app' && cufConfig.app && cufConfig.app.variantId && !trialCovered(cartHasTrial, kitItems, selectedKit)) steps.push(name);
    });

    return {
      cufRoot: cufRoot,
      cufConfig: cufConfig,
      croConfig: croConfig,
      kitItems: kitItems,
      kitVariantId: Number(variantId),
      selectedKit: selectedKit,
      cartHasTrial: cartHasTrial,
      steps: steps
    };
  }

  // The config's cartHasTrial is page-load state, and the cart may have
  // changed since (a trial removed in the drawer). Re-read it on open: add
  // the app step if it is now owed, and let next() drop it if a trial
  // turned up.
  function refreshCartTrial(ctx) {
    var appId = ctx.cufConfig.app && Number(ctx.cufConfig.app.variantId);
    if (!appId) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (state.ctx !== ctx || !state.open || state.busy) return;
        ctx.cartHasTrial = (cart.items || []).some(function (it) { return it.variant_id === appId; });
        var owed = (ctx.cufConfig.stepOrder || []).indexOf('app') !== -1 &&
          !trialCovered(ctx.cartHasTrial, ctx.kitItems.concat(state.accepted), ctx.selectedKit);
        if (owed && ctx.steps.indexOf('app') === -1) {
          ctx.steps.push('app');
          buildDots();
          var label = ctx.cufRoot.querySelector('[data-cuf-progress-label]');
          if (label) label.textContent = (state.stepIndex + 1) + ' of ' + ctx.steps.length;
          ctx.cufRoot.querySelectorAll('.cuf__dot').forEach(function (dot, idx) {
            dot.classList.toggle('cuf__dot--active', idx === state.stepIndex);
            dot.classList.toggle('cuf__dot--done', idx < state.stepIndex);
          });
        }
      })
      .catch(function () {});
  }

  // ------------------------------------------------------------------
  // Intercept: capture-phase, runs before the button-level listeners in
  // cro-v1-pdp.js. Any failure here leaves the legacy path untouched.
  // ------------------------------------------------------------------
  function onDocClickCapture(e) {
    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    var btn = target.closest('[data-cro-add-to-cart]');
    if (!btn) return;
    if (!document.querySelector('[data-cuf-root]')) return; // feature detect: no snippet, no intercept

    if (state.open || state.busy) {
      // A flow is already in progress; swallow duplicate clicks.
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    var ctx = null;
    try {
      ctx = buildContext();
    } catch (err) {
      console.warn('[cuf] context build failed, using legacy direct add', err);
      ctx = null;
    }
    if (!ctx || !ctx.steps.length) return; // fall through: cro-v1-pdp.js adds the kit directly

    e.preventDefault();
    e.stopImmediatePropagation();

    state.kitAdd = addKitNow(ctx);

    try {
      openFlow(ctx, btn);
      refreshCartTrial(ctx);
    } catch (err) {
      console.warn('[cuf] failed to open upsell flow, adding kit directly', err);
      var pendingKit = state.kitAdd; // hardClose() clears it; keep the in-flight add
      try { hardClose(); } catch (e2) {}
      state.kitAdd = pendingKit;
      state.ctx = ctx;
      state.accepted = [];
      state.triggerBtn = btn;
      finalize();
    }
  }

  // ------------------------------------------------------------------
  // Overlay controls: bubble-phase delegation so re-rendered DOM
  // (after a color swap) keeps working without rebinding.
  // ------------------------------------------------------------------
  function onDocClickBubble(e) {
    if (!state.open) return;
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    var root = state.ctx && state.ctx.cufRoot;
    if (!root || !root.contains(t)) return;

    var el;
    if ((el = t.closest('[data-cuf-error-retry]'))) { e.preventDefault(); finalize(); return; }
    if ((el = t.closest('[data-cuf-error-close]'))) { e.preventDefault(); setBusy(false); hardClose(); return; }
    if ((el = t.closest('[data-cuf-close]'))) { e.preventDefault(); if (!state.busy) requestClose(); return; }
    if ((el = t.closest('[data-cuf-skip]'))) { e.preventDefault(); if (!state.busy) skipStep(); return; }
    if ((el = t.closest('[data-cuf-fc]'))) { e.preventDefault(); selectFc(el.getAttribute('data-cuf-fc')); return; }
    if ((el = t.closest('[data-cuf-kit]'))) { e.preventDefault(); selectCrossKit(el.getAttribute('data-cuf-kit')); return; }
    if ((el = t.closest('[data-cuf-wb]'))) { e.preventDefault(); selectWb(el.getAttribute('data-cuf-wb')); return; }
    if ((el = t.closest('[data-cuf-accept]'))) { e.preventDefault(); if (!state.busy) acceptStep(el.getAttribute('data-cuf-accept')); return; }
  }

  function onKeydown(e) {
    if (!state.open) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      e.stopPropagation();
      if (!state.busy) requestClose();
      return;
    }
    if (e.key === 'Tab') {
      var sheet = state.ctx && state.ctx.cufRoot.querySelector('.cuf__sheet');
      if (!sheet) return;
      var nodes = sheet.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      var list = Array.prototype.filter.call(nodes, function (el) {
        return el.offsetParent !== null && !el.disabled;
      });
      if (!list.length) return;
      var first = list[0];
      var last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  // ------------------------------------------------------------------
  // Flow lifecycle
  // ------------------------------------------------------------------
  function openFlow(ctx, btn) {
    state.ctx = ctx;
    state.open = true;
    state.stepIndex = 0;
    state.accepted = [];
    state.triggerBtn = btn;
    state.lastFocus = document.activeElement;

    // Default selections
    state.selectedFc = null;
    var fcs = ctx.cufConfig.flashcards || [];
    if (fcs.length) {
      var pre = ctx.cufConfig.preselectFlashcard;
      var match = fcs.filter(function (f) { return f.handle === pre; })[0];
      state.selectedFc = match || fcs[0];
    }
    state.selectedWb = null;
    var wbTiers = (ctx.cufConfig.workbook && ctx.cufConfig.workbook.tiers) || [];
    if (wbTiers.length) {
      var flagged = wbTiers.filter(function (t) { return t.flag === 'Most Popular'; })[0];
      state.selectedWb = flagged || wbTiers[0];
    }
    state.selectedCrossKit = null;
    var kitItems = (ctx.cufConfig.kits && ctx.cufConfig.kits.items) || [];
    if (kitItems.length) {
      var preKit = ctx.cufConfig.kits.preselect;
      var kitMatch = kitItems.filter(function (k) { return k.handle === preKit; })[0];
      state.selectedCrossKit = kitMatch || kitItems[0];
    }

    buildDots();
    syncFcUI();
    syncWbUI();
    syncCrossKitUI();
    resetBusyPane();

    // Prewarm the images the cart drawer will show, so it never opens with
    // blank thumbnail boxes. Fire-and-forget; failures are harmless.
    try {
      var warm = [];
      (ctx.cufConfig.flashcards || []).forEach(function (f) { if (f.image) warm.push(f.image); });
      ((ctx.cufConfig.kits && ctx.cufConfig.kits.items) || []).forEach(function (k) { if (k.image) warm.push(k.image); });
      if (ctx.cufConfig.workbook && ctx.cufConfig.workbook.image) warm.push(ctx.cufConfig.workbook.image);
      if (ctx.cufConfig.pd && ctx.cufConfig.pd.image) warm.push(ctx.cufConfig.pd.image);
      if (ctx.cufConfig.app && ctx.cufConfig.app.image) warm.push(ctx.cufConfig.app.image);
      document.querySelectorAll('.cro-hero__thumb img, .cro-hero__main img').forEach(function (im) {
        if (im.currentSrc || im.src) warm.push(im.currentSrc || im.src);
      });
      warm.slice(0, 12).forEach(function (u) { var im = new Image(); im.src = u; });
    } catch (e) {}

    var root = ctx.cufRoot;
    root.hidden = false;
    state.prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () {
      root.setAttribute('data-open', 'true');
    });
    // rAF can be throttled in background tabs; make sure the sheet still shows.
    setTimeout(function () {
      if (state.open) root.setAttribute('data-open', 'true');
    }, 120);

    showStep(0);
    document.addEventListener('keydown', onKeydown, true);
    fireFlowEvent('cro-upsell:open', { kitVariantId: ctx.kitVariantId, steps: ctx.steps });
  }

  function currentStepName() {
    var ctx = state.ctx;
    return (ctx && ctx.steps[state.stepIndex]) || null;
  }

  function showStep(i) {
    var ctx = state.ctx;
    var root = ctx.cufRoot;
    var name = ctx.steps[i];

    root.querySelectorAll('[data-cuf-step]').forEach(function (pane) {
      var isTarget = pane.getAttribute('data-cuf-step') === name;
      if (isTarget) pane.removeAttribute('hidden');
      else pane.setAttribute('hidden', '');
    });

    var label = root.querySelector('[data-cuf-progress-label]');
    if (label) label.textContent = (i + 1) + ' of ' + ctx.steps.length;
    root.querySelectorAll('.cuf__dot').forEach(function (dot, idx) {
      dot.classList.toggle('cuf__dot--active', idx === i);
      dot.classList.toggle('cuf__dot--done', idx < i);
    });

    var pane = root.querySelector('[data-cuf-step="' + name + '"]');
    if (pane) {
      var primary = pane.querySelector('[data-cuf-accept]');
      if (primary) {
        requestAnimationFrame(function () {
          try { primary.focus({ preventScroll: true }); } catch (e) {}
        });
      }
      var sheet = root.querySelector('.cuf__sheet');
      if (sheet) sheet.scrollTop = 0;
    }

    fireFlowEvent('cro-upsell:step-view', { step: name, index: i });
  }

  function buildDots() {
    var ctx = state.ctx;
    var dotsEl = ctx.cufRoot.querySelector('[data-cuf-dots]');
    if (!dotsEl) return;
    while (dotsEl.firstChild) dotsEl.removeChild(dotsEl.firstChild);
    ctx.steps.forEach(function () {
      var d = document.createElement('span');
      d.className = 'cuf__dot';
      dotsEl.appendChild(d);
    });
  }

  // ------------------------------------------------------------------
  // Step 1: flashcards colorway selection
  // ------------------------------------------------------------------
  function selectFc(handle) {
    var fcs = (state.ctx && state.ctx.cufConfig.flashcards) || [];
    var match = fcs.filter(function (f) { return f.handle === handle; })[0];
    if (!match) return;
    state.selectedFc = match;
    syncFcUI();
  }

  function syncFcUI() {
    var ctx = state.ctx;
    if (!ctx || !state.selectedFc) return;
    var root = ctx.cufRoot;
    var fc = state.selectedFc;

    root.querySelectorAll('[data-cuf-fc]').forEach(function (b) {
      var on = b.getAttribute('data-cuf-fc') === fc.handle;
      b.classList.toggle('cuf__fc--active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    var nameEl = root.querySelector('[data-cuf-fc-selected-name]');
    if (nameEl) nameEl.textContent = fc.colorName + ' · Value Pack';

    var priceEl = root.querySelector('[data-cuf-fc-price]');
    if (priceEl) priceEl.textContent = fmt(fc.price);

    var cmpEl = root.querySelector('[data-cuf-fc-compare]');
    var saveEl = root.querySelector('[data-cuf-fc-save]');
    var hasCompare = typeof fc.compareAt === 'number' && fc.compareAt > fc.price;
    if (cmpEl) {
      cmpEl.textContent = hasCompare ? fmt(fc.compareAt) : '';
      cmpEl.style.display = hasCompare ? '' : 'none';
    }
    if (saveEl) {
      saveEl.textContent = hasCompare ? 'You save ' + fmt(fc.compareAt - fc.price) : '';
      saveEl.style.display = hasCompare ? '' : 'none';
    }

    var acceptBtn = root.querySelector('[data-cuf-accept="flashcards"]');
    if (acceptBtn) acceptBtn.textContent = 'Add Flashcards · ' + fmt(fc.price);
  }

  // ------------------------------------------------------------------
  // Kit cross-sell step: colorway selection (flashcards + workbook hosts)
  // ------------------------------------------------------------------
  function selectCrossKit(handle) {
    var kits = (state.ctx && state.ctx.cufConfig.kits && state.ctx.cufConfig.kits.items) || [];
    var match = kits.filter(function (k) { return k.handle === handle; })[0];
    if (!match) return;
    state.selectedCrossKit = match;
    syncCrossKitUI();
  }

  function syncCrossKitUI() {
    var ctx = state.ctx;
    if (!ctx || !state.selectedCrossKit) return;
    var root = ctx.cufRoot;
    var kit = state.selectedCrossKit;

    root.querySelectorAll('[data-cuf-kit]').forEach(function (b) {
      var on = b.getAttribute('data-cuf-kit') === kit.handle;
      b.classList.toggle('cuf__fc--active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    var nameEl = root.querySelector('[data-cuf-kit-selected-name]');
    if (nameEl) nameEl.textContent = kit.colorName + ' · Value Pack';

    var priceEl = root.querySelector('[data-cuf-kit-price]');
    if (priceEl) priceEl.textContent = fmt(kit.price);

    var cmpEl = root.querySelector('[data-cuf-kit-compare]');
    var saveEl = root.querySelector('[data-cuf-kit-save]');
    var hasCompare = typeof kit.compareAt === 'number' && kit.compareAt > kit.price;
    if (cmpEl) {
      cmpEl.textContent = hasCompare ? fmt(kit.compareAt) : '';
      cmpEl.style.display = hasCompare ? '' : 'none';
    }
    if (saveEl) {
      saveEl.textContent = hasCompare ? 'You save ' + fmt(kit.compareAt - kit.price) : '';
      saveEl.style.display = hasCompare ? '' : 'none';
    }

    var acceptBtn = root.querySelector('[data-cuf-accept="kits"]');
    if (acceptBtn) acceptBtn.textContent = 'Add Class Kit · ' + fmt(kit.price);
  }

  // ------------------------------------------------------------------
  // Step 2: workbook volume tier selection
  // ------------------------------------------------------------------
  function selectWb(id) {
    var tiers = (state.ctx && state.ctx.cufConfig.workbook && state.ctx.cufConfig.workbook.tiers) || [];
    var match = tiers.filter(function (t) { return String(t.id) === String(id); })[0];
    if (!match) return;
    state.selectedWb = match;
    syncWbUI();
  }

  function syncWbUI() {
    var ctx = state.ctx;
    if (!ctx || !state.selectedWb) return;
    var root = ctx.cufRoot;
    var tier = state.selectedWb;

    root.querySelectorAll('[data-cuf-wb]').forEach(function (b) {
      var on = String(b.getAttribute('data-cuf-wb')) === String(tier.id);
      b.classList.toggle('cuf__wb--active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    var acceptBtn = root.querySelector('[data-cuf-accept="workbook"]');
    if (acceptBtn) acceptBtn.textContent = 'Add ' + tier.label + ' · ' + fmt(tier.price);
  }

  // ------------------------------------------------------------------
  // Accept / skip / close
  // ------------------------------------------------------------------
  function acceptStep(kind) {
    var ctx = state.ctx;
    if (!ctx) return;
    if (kind === 'flashcards' && state.selectedFc) {
      state.accepted.push({ id: Number(state.selectedFc.variantId), quantity: 1 });
    } else if (kind === 'kits' && state.selectedCrossKit) {
      state.accepted.push({ id: Number(state.selectedCrossKit.variantId), quantity: 1 });
    } else if (kind === 'workbook' && state.selectedWb) {
      state.accepted.push({ id: Number(state.selectedWb.id), quantity: 1 });
    } else if (kind === 'soundcards' && ctx.cufConfig.soundcards) {
      state.accepted.push({ id: Number(ctx.cufConfig.soundcards.variantId), quantity: 1 });
    } else if (kind === 'pd' && ctx.cufConfig.pd) {
      state.accepted.push({ id: Number(ctx.cufConfig.pd.variantId), quantity: 1 });
    } else if (kind === 'app' && ctx.cufConfig.app) {
      state.accepted.push({
        id: Number(ctx.cufConfig.app.variantId),
        quantity: 1,
        selling_plan: Number(ctx.cufConfig.app.sellingPlanId)
      });
    }
    fireFlowEvent('cro-upsell:accept', { step: kind });
    next();
  }

  // Busy visibility: while an add-to-cart is in flight, dim and disable the
  // skip/close controls. Clarity showed 39% of "No thanks, continue" clicks
  // dying silently against the state.busy guard while shoppers waited.
  function setBusy(v) {
    state.busy = v;
    var root = state.ctx && state.ctx.cufRoot;
    if (root) {
      var sheet = root.querySelector('.cuf__sheet');
      if (sheet) sheet.classList.toggle('cuf--busy', !!v);
    }
  }

  function skipStep() {
    fireFlowEvent('cro-upsell:skip', { step: currentStepName() });
    next();
  }

  function next() {
    var ctx = state.ctx;
    state.stepIndex += 1;
    // An accepted Value Pack add-on (e.g. flashcards) brings the trial with
    // it, so the app step would be a repeat offer.
    if (ctx.steps[state.stepIndex] === 'app' &&
        trialCovered(ctx.cartHasTrial, ctx.kitItems.concat(state.accepted), ctx.selectedKit)) {
      ctx.steps.splice(state.stepIndex, 1);
      buildDots();
    }
    if (state.stepIndex >= state.ctx.steps.length) finalize();
    else showStep(state.stepIndex);
  }

  // Escape / backdrop / X: skip everything remaining, keep what was accepted,
  // and add the kit + accepted upsells right away.
  function requestClose() {
    fireFlowEvent('cro-upsell:dismiss', { step: currentStepName(), accepted: state.accepted.length });
    finalize();
  }

  // ------------------------------------------------------------------
  // Final batch add: kit item(s) + accepted upsells in ONE /cart/add.js
  // POST, then the same drawer + analytics flow as cro-v1-pdp.js.
  // ------------------------------------------------------------------
  function postAdd(items, sectionsList) {
    var payload = { items: items };
    if (sectionsList !== null && sectionsList !== '') {
      payload.sections = sectionsList;
      payload.sections_url = window.location.pathname;
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.json()
        .then(function (data) { return { ok: r.ok, data: data }; })
        .catch(function () { return { ok: r.ok, data: null }; });
    });
  }

  // Analytics/theme events for an add, same set cro-v1-pdp.js fires.
  function fireAddedEvents(data, ctx) {
    try {
      window.dispatchEvent(new CustomEvent('cro-v1:added-to-cart', { detail: data }));
      document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true, detail: data }));
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'cro-v1-pdp',
          productVariantId: ctx.kitVariantId,
          cartData: data
        });
      }
    } catch (e) {}
  }

  // The kit goes in on the click, before the first upsell step. A shopper
  // who swipes back or closes the tab mid-ladder keeps it in the cart, so
  // the abandoned-cart email can fire and Meta sees the AddToCart it
  // optimizes on. No sections are requested: the drawer stays closed until
  // finalize() renders it.
  function addKitNow(ctx) {
    return postAdd(ctx.kitItems, null)
      .then(function (res) {
        if (res.ok) fireAddedEvents(res.data, ctx);
        else console.warn('[cuf] early kit add failed, finalize will retry it', res.data);
        return res;
      })
      .catch(function (err) {
        console.warn('[cuf] early kit add failed, finalize will retry it', err);
        return { ok: false, data: null };
      });
  }

  // Fresh drawer sections for a render that had no add-on POST to carry
  // them (every step skipped, or add-ons retried one by one). A no-op cart
  // update returns the same sections payload an add does.
  function refreshSections(sectionsList) {
    if (!sectionsList) return Promise.resolve(null);
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ updates: {}, sections: sectionsList, sections_url: window.location.pathname })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { return d && d.sections ? d : null; })
      .catch(function () { return null; });
  }

  // One bad add-on line must not sink the others: add them one at a time.
  function addEach(items) {
    var anyOk = false;
    return items.reduce(function (chain, item) {
      return chain.then(function () {
        return postAdd([item], null)
          .then(function (res) { if (res.ok) anyOk = true; })
          .catch(function () {});
      });
    }, Promise.resolve()).then(function () { return anyOk; });
  }

  function finalize() {
    if (state.busy) return;
    var ctx = state.ctx;
    if (!ctx) return;
    setBusy(true);
    showBusyPane();

    var accepted = state.accepted.slice();

    // Match the Dawn pattern used by cro-v1-pdp.js: ask for the drawer
    // sections back so renderContents() can re-render AND open the drawer.
    var cartDrawer = document.querySelector('cart-drawer');
    var cartNotification = document.querySelector('cart-notification');
    var renderTarget = cartDrawer || cartNotification;
    var sectionsList = null;
    if (renderTarget && typeof renderTarget.getSectionsToRender === 'function') {
      try {
        sectionsList = renderTarget.getSectionsToRender().map(function (s) { return s.id; }).join(',');
      } catch (e) { sectionsList = null; }
    }

    var btn = state.triggerBtn;
    var origText = btn ? btn.textContent : '';
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Adding…';
    }

    (state.kitAdd || Promise.resolve({ ok: false, data: null }))
      .then(function (kitRes) {
        if (!kitRes.ok) {
          // The early kit add failed: send kit + add-ons as one batch, and
          // never lose the kit purchase over a bad upsell line.
          return postAdd(ctx.kitItems.concat(accepted), sectionsList)
            .then(function (res) {
              if (!res.ok && accepted.length) {
                console.warn('[cuf] batch add failed, retrying kit only', res.data);
                fireFlowEvent('cro-upsell:batch-retry', { failed: res.data });
                return postAdd(ctx.kitItems, sectionsList);
              }
              return res;
            })
            .then(function (res) {
              if (res.ok) state.kitAdd = Promise.resolve(res);
              res.fireEvents = res.ok;
              return res;
            });
        }
        if (!accepted.length) {
          return refreshSections(sectionsList).then(function (data) {
            return { ok: true, data: data, fireEvents: false };
          });
        }
        return postAdd(accepted, sectionsList).then(function (res) {
          if (res.ok) { res.fireEvents = true; return res; }
          console.warn('[cuf] add-on batch failed, adding one at a time', res.data);
          fireFlowEvent('cro-upsell:batch-retry', { failed: res.data });
          return addEach(accepted).then(function (anyOk) {
            return refreshSections(sectionsList).then(function (data) {
              return { ok: true, data: data, fireEvents: anyOk };
            });
          });
        });
      })
      .then(function (res) {
        if (!res.ok) {
          var err = new Error('cart add failed');
          err.data = res.data;
          throw err;
        }
        onAdded(res.data, res.fireEvents, btn, origText, renderTarget, ctx);
      })
      .catch(function (err) {
        console.warn('[cuf] cart add error', err);
        setBusy(false);
        showErrorPane();
        if (btn) {
          btn.textContent = 'Error · try again';
          setTimeout(function () {
            btn.textContent = origText;
            btn.disabled = false;
          }, 2400);
        }
      });
  }

  function onAdded(data, fireEvents, btn, origText, renderTarget, ctx) {
    // The kit's add events already fired in addKitNow(); fire again only for
    // a POST that actually added something here.
    if (fireEvents) fireAddedEvents(data, ctx);
    fireFlowEvent('cro-upsell:completed', { acceptedCount: state.accepted.length });

    var didOpenDrawer = false;
    if (renderTarget && typeof renderTarget.renderContents === 'function') {
      // The custom drawer cannot parse multi-item add responses, so always
      // refetch its sections fresh and feed renderContents the exact shape
      // it expects: { sections: { '<id>': '<html>' } }.
      // finalize() always hands over a payload carrying the drawer sections
      // (from the add-on POST or refreshSections()). The synchronous XHR
      // refetch that used to live here came back with status 0 in Chrome.
      var freshState = null;
      if (data && data.sections && data.sections['cart-drawer']) {
        freshState = { sections: data.sections, id: data.id };
        if (typeof data.item_count === 'number') freshState.item_count = data.item_count;
      }
      try {
        renderTarget.renderContents(freshState || data);
        didOpenDrawer = true;
      } catch (e) {
        console.warn('[cuf] renderContents failed', e);
      }
    }
    // Multi-item add responses can render the custom drawer BLANK (it opens
    // before it fails to parse). Detect an empty drawer and fall back.
    if (didOpenDrawer && renderTarget) {
      // Check CONTENT (textContent works on hidden elements; the drawer
      // populates while hidden and opens on a later tick). If the markup is
      // genuinely empty, wait out the open tick, then fall back to /cart.
      try {
        var innerEl = document.getElementById('CartDrawer') || renderTarget;
        var innerTxt = (innerEl.textContent || '').trim();
        if (innerTxt.length < 20) {
          didOpenDrawer = false;
          setTimeout(function () {
            var again = ((document.getElementById('CartDrawer') || renderTarget).textContent || '').trim();
            if (again.length < 20) {
              renderTarget.classList.remove('active');
              renderTarget.classList.remove('animate');
              document.body.classList.remove('overflow-hidden');
              console.warn('[cuf] drawer rendered blank, falling back to /cart');
              window.location.href = '/cart';
            }
          }, 600);
        }
      } catch (e) {}
    }
    if (!didOpenDrawer) {
      var opener = document.querySelector('[data-cart-drawer-open], .cart-drawer-toggle, [aria-controls="CartDrawer"]');
      if (opener && typeof opener.click === 'function') {
        opener.click();
        didOpenDrawer = true;
      }
    }

    var fallbackNavigate = !!(ctx.croConfig && ctx.croConfig.fallbackNavigateToCart);
    // Close the overlay only after the drawer has had a couple of frames to
    // paint its freshly rendered content, so there is no blank-drawer flash.
    if (didOpenDrawer) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          setTimeout(hardClose, 120);
        });
      });
      // Backstop in case rAF is throttled (background tab).
      setTimeout(function () { try { hardClose(); } catch (e) {} }, 900);
    } else {
      hardClose();
    }
    setBusy(false);

    if (btn) {
      btn.textContent = '✓ Added to cart';
      setTimeout(function () {
        btn.textContent = origText;
        btn.disabled = false;
        if (!didOpenDrawer && fallbackNavigate) window.location.href = '/cart';
      }, 1100);
    } else if (!didOpenDrawer && fallbackNavigate) {
      window.location.href = '/cart';
    }
  }

  // ------------------------------------------------------------------
  // Busy / error panes + teardown
  // ------------------------------------------------------------------
  function resetBusyPane() {
    var root = state.ctx && state.ctx.cufRoot;
    if (!root) return;
    var busyMsg = root.querySelector('[data-cuf-busy]');
    var errBox = root.querySelector('[data-cuf-error]');
    if (busyMsg) busyMsg.removeAttribute('hidden');
    if (errBox) errBox.setAttribute('hidden', '');
  }

  function showBusyPane() {
    var root = state.ctx && state.ctx.cufRoot;
    if (!root) return;
    resetBusyPane();
    root.querySelectorAll('[data-cuf-step]').forEach(function (pane) {
      if (pane.getAttribute('data-cuf-step') === 'busy') pane.removeAttribute('hidden');
      else pane.setAttribute('hidden', '');
    });
  }

  function showErrorPane() {
    var root = state.ctx && state.ctx.cufRoot;
    if (!root) return;
    showBusyPane();
    var busyMsg = root.querySelector('[data-cuf-busy]');
    var errBox = root.querySelector('[data-cuf-error]');
    if (busyMsg) busyMsg.setAttribute('hidden', '');
    if (errBox) errBox.removeAttribute('hidden');
  }

  function hardClose() {
    var root = state.ctx && state.ctx.cufRoot;
    if (root) {
      root.removeAttribute('data-open');
      root.hidden = true;
    }
    document.body.style.overflow = state.prevBodyOverflow || '';
    document.removeEventListener('keydown', onKeydown, true);
    state.open = false;
    state.accepted = [];
    state.stepIndex = 0;
    state.kitAdd = null;
    if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
      try { state.lastFocus.focus(); } catch (e) {}
    }
    state.lastFocus = null;
  }

  // ------------------------------------------------------------------
  // Init: listeners live on document so they survive the section
  // re-render performed by the color swap in cro-v1-pdp.js.
  // ------------------------------------------------------------------
  ready(function () {
    document.addEventListener('click', onDocClickCapture, true);
    document.addEventListener('click', onDocClickBubble, false);
  });
})();
